import React, { useState, useEffect, useCallback } from "react";
import { Box, Grid, Typography, Pagination, Stack } from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import TaxCodeCard from "../components/taxCodes/TaxCodeCard";
import CreateTaxCodeModal from "../components/taxCodes/CreateTaxCodeModal";
import UpdateTaxCodeModal from "../components/taxCodes/UpdateTaxCodeModal";
import AppCircularProgress from "../components/common/AppCircularProgress";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getTaxCodes,
  deleteTaxCode,
  resetTaxCodeServerStatus,
} from "../redux/actions/taxCodeActions";
import PageError from "../components/common/PageError";
import { TaxCodeResponseDto } from "../shared/dtos/taxCode/TaxCodeResponseDto";

const TaxCodesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: taxCodes,
    pagination,
    loadingFetchAll: loadingTaxCodes,
    errorFetchAll: errorLoadingTaxCodes,
    serverStatus,
    loadingDelete: loadingTaxCodeDelete,
    loadingUpdate: loadingTaxCodeUpdate,
    loadingCreate: loadingTaxCodeCreate,
    errorCreate: errorCreatingTaxCode,
    errorUpdate: errorUpdatingTaxCode,
  } = useAppSelector((state) => state.taxCode);

  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(12);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedTaxCode, setSelectedTaxCode] =
    useState<TaxCodeResponseDto | null>(null);

  const canCreate = hasPermission("tax_code:create");
  const canUpdate = hasPermission("tax_code:update");
  const canDelete = hasPermission("tax_code:delete");

  const fetchTaxCodes = useCallback(() => {
    dispatch(getTaxCodes({ page, size: limit, search: debouncedSearch }));
  }, [dispatch, page, limit, debouncedSearch]);

  useEffect(() => {
    fetchTaxCodes();
  }, [fetchTaxCodes]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback(
    () => !loadingTaxCodeCreate && setIsCreateModalOpen(false),
    [loadingTaxCodeCreate],
  );

  const handleOpenUpdate = (item: TaxCodeResponseDto): void => {
    setSelectedTaxCode(item);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback(() => {
    if (!loadingTaxCodeUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedTaxCode(null);
    }
  }, [loadingTaxCodeUpdate]);

  const handleOpenDelete = (item: TaxCodeResponseDto): void => {
    setSelectedTaxCode(item);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback(() => {
    if (!loadingTaxCodeDelete) {
      setIsDeleteModalOpen(false);
      setSelectedTaxCode(null);
    }
  }, [loadingTaxCodeDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedTaxCode) dispatch(deleteTaxCode(selectedTaxCode.id));
  };

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;
    if (operation === "create" && (status === 201 || status === 200))
      handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();
    dispatch(resetTaxCodeServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  const renderContent = (): React.JSX.Element => {
    if (loadingTaxCodes) return <AppCircularProgress />;
    if (errorLoadingTaxCodes)
      return <PageError error={errorLoadingTaxCodes} onRetry={fetchTaxCodes} />;

    if (!taxCodes || taxCodes.length === 0)
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography>No tax codes found.</Typography>
        </Box>
      );

    return (
      <>
        <Grid container spacing={3}>
          {taxCodes.map((item) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={item.id}>
              <TaxCodeCard
                taxCode={item}
                onEdit={canUpdate ? () => handleOpenUpdate(item) : undefined}
                onDelete={canDelete ? () => handleOpenDelete(item) : undefined}
              />
            </Grid>
          ))}
        </Grid>
        {pagination && pagination.totalPages! > 1 && (
          <Stack sx={{ alignItems: "center", mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(_e, v) => setPage(v)}
              color="primary"
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer
      title="Tax Codes"
      subtitle="Define value added tax rates and other tax categories."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Add Tax Code
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search tax codes..."
      />
      {renderContent()}

      {isCreateModalOpen && (
        <CreateTaxCodeModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingTaxCodeCreate}
          error={errorCreatingTaxCode}
        />
      )}
      {isUpdateModalOpen && selectedTaxCode && (
        <UpdateTaxCodeModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          taxCode={selectedTaxCode}
          loading={loadingTaxCodeUpdate}
          error={errorUpdatingTaxCode}
        />
      )}
      {isDeleteModalOpen && selectedTaxCode && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Tax Code"
          description={`Are you sure you want to delete the tax code "${selectedTaxCode.name}"?`}
          loading={loadingTaxCodeDelete}
          severity="error"
        />
      )}
    </PageContainer>
  );
};

export default TaxCodesPage;
